import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface LargePortraitLinkProps {
  link: LinkViewModel
  className?: string
  isGuildMember?: boolean
  loading?: boolean
  isGrid?: boolean
}

export function LargePortraitLink(props: LargePortraitLinkProps) {
  const track = useSafeTrack()
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const cardRef = useRef<HTMLDivElement>(null)
  const { className, isGuildMember = false, link } = props

  const handleClick = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  if (!link.imageUrl) return null

  return (
    <div onClick={handleClick} ref={cardRef} className="relative">
      <InternalLink
        href={link.linkUrl}
        aria-label={`project-tile-${link.name}`}
        className={classNames(
          'min-w-0 relative block overflow-hidden rounded-lg duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.03]',
          { 'inline-block h-fit': props.isGrid },
          className,
        )}
      >
        <When condition={props.isGrid}>
          <Image
            aria-label={link.alt}
            alt={link.alt}
            src={getCloudinaryImageUrl({
              path: link.imageUrl,
              transforms: 'w_294',
            })}
            height={490}
            width={294}
            className="rounded-lg bg-black"
          />
          <GuildAccessNotch
            isInEarlyAccess={link.isInEarlyAccess}
            isGuildMember={isGuildMember}
            loading={props.loading}
          />
        </When>
        <When condition={!props.isGrid}>
          <div className={classNames('relative w-full rounded-lg bg-black', isLoadingImage ? 'animate-pulse' : '')}>
            <div className="relative w-full" style={{ paddingTop: '160%' }}>
              <Image
                aria-label={link.alt}
                alt={link.alt}
                src={getCloudinaryImageUrl({
                  path: link.imageUrl,
                  transforms: 'w_294',
                })}
                fill
                sizes="(min-width: 1024px) 294px, 33vw"
                className="absolute inset-0 rounded-lg object-cover"
                onLoadingComplete={() => setIsLoadingImage(false)}
              />
              <GuildAccessNotch
                isInEarlyAccess={link.isInEarlyAccess}
                isGuildMember={isGuildMember}
                loading={props.loading}
              />
            </div>
          </div>
        </When>
      </InternalLink>
    </div>
  )
}
